import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { MagnifyingGlass, IdentificationBadge, WarningCircle, Wrench, Ruler, ListChecks, ShieldCheck, Lifebuoy } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/customer/PageContainer";
import { ReportDiscrepancyModal } from "@/components/customer/ReportDiscrepancyModal";
import { useCustomer } from "@/context/CustomerContext";
import { getErrorMessage } from "@/lib/api";
import { MeasurementsPanel, StandardFeatures } from "@/components/customer/ModelSpecs";
import { COLUMN_GROUPS, OPTIONS_GROUP, SpecGroup, ExtraDetails } from "@/components/customer/caravan/buildFields";
import { CaravanHero } from "@/components/customer/caravan/CaravanHero";
import { CaravanTabs } from "@/components/customer/caravan/CaravanTabs";
import { WarrantyHistory, useWarrantyClaims } from "@/components/customer/caravan/WarrantyHistory";
import { SupportTickets, useSupportTickets } from "@/components/customer/caravan/SupportTickets";

const DEMO_CHASSIS = ["OTM123456", "OTM123457", "OTM123458"];

function LookupForm({ onLookup, loading }) {
  const [chassis, setChassis] = useState("");
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg">
      <form onSubmit={(e) => { e.preventDefault(); onLookup(chassis); }} className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-7 shadow-2xl">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-9 h-9 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
            <IdentificationBadge size={20} className="text-[#73bf44]" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-semibold text-white leading-tight">Find your caravan</div>
            <div className="text-xs text-neutral-500">Your chassis number is on the compliance plate</div>
          </div>
        </div>
        <label className="text-sm text-neutral-400 mb-2 block">Chassis Number</label>
        <div className="flex items-center gap-3 bg-black/60 border border-neutral-700 rounded-xl px-4 py-3.5 focus-within:border-[#73bf44] focus-within:ring-1 focus-within:ring-[#73bf44] transition-all mb-4">
          <MagnifyingGlass size={18} className="text-neutral-500" />
          <input data-testid="my-caravan-chassis-input" value={chassis} onChange={(e) => setChassis(e.target.value)} placeholder="e.g. OTM123456" className="bg-transparent outline-none text-white w-full placeholder:text-neutral-600 font-mono tracking-widest" />
        </div>
        <button type="submit" disabled={loading} data-testid="my-caravan-lookup-button" className="w-full bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold py-3.5 hover:bg-[#5fa338] transition-colors disabled:opacity-60">
          {loading ? "Looking up…" : "Find My Caravan"}
        </button>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-600">Try a demo:</span>
          {DEMO_CHASSIS.map((c) => (
            <button key={c} type="button" data-testid={`demo-chassis-${c}`} onClick={() => { setChassis(c); onLookup(c); }} className="font-mono text-xs px-3 py-1.5 rounded-full border border-neutral-700 text-neutral-300 hover:border-[#73bf44] hover:text-[#73bf44] transition-colors">
              {c}
            </button>
          ))}
        </div>
      </form>
    </motion.div>
  );
}

function ReportCard({ onReport }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#0F0F0F] border border-dashed border-neutral-800 rounded-2xl px-5 py-4">
      <div className="flex items-start gap-2.5">
        <WarningCircle size={18} className="text-amber-400 mt-0.5 shrink-0" weight="duotone" />
        <div>
          <div className="text-sm font-medium text-white">Something not right?</div>
          <div className="text-xs text-neutral-500">If any of these details don't match your caravan, let us know and we'll review and fix it.</div>
        </div>
      </div>
      <button onClick={onReport} data-testid="report-discrepancy-open" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-neutral-700 text-neutral-200 px-4 py-2.5 text-sm hover:border-amber-400/60 hover:text-amber-300 transition-colors">
        <WarningCircle size={16} weight="fill" /> Report incorrect details
      </button>
    </div>
  );
}

function BuildTab({ caravan, onCaravanChange, onReport }) {
  return (
    <div className="space-y-5">
      <div className="lg:columns-2 gap-5 [&>*]:mb-5 [&>*]:break-inside-avoid">
        {COLUMN_GROUPS.map((g, i) => <SpecGroup key={g.id} group={g} caravan={caravan} index={i} onCaravanChange={onCaravanChange} />)}
      </div>
      <SpecGroup group={OPTIONS_GROUP} caravan={caravan} index={COLUMN_GROUPS.length} />
      <ExtraDetails caravan={caravan} />
      <ReportCard onReport={onReport} />
    </div>
  );
}

function buildTabs(caravan, warranty, support) {
  const specs = caravan.model_specs;
  return [
    { id: "build", label: "Your Build", icon: Wrench },
    specs?.key_specs?.length && { id: "specs", label: "Measurements & Weights", icon: Ruler },
    specs?.groups?.length && { id: "features", label: "Standard Features", icon: ListChecks, count: specs.groups.reduce((n, g) => n + g.items.length, 0) },
    { id: "warranty", label: "Warranty", icon: ShieldCheck, count: warranty.loading || warranty.error ? null : warranty.claims.length },
    { id: "support", label: "Support", icon: Lifebuoy, count: support.loading || support.error ? null : support.tickets.length },
  ].filter(Boolean);
}

function CaravanDetails({ caravan, onCaravanChange, onClear }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("build");
  const [reportOpen, setReportOpen] = useState(false);
  const warranty = useWarrantyClaims(caravan.chassis_number);
  const support = useSupportTickets(caravan.chassis_number);
  const tabs = buildTabs(caravan, warranty, support);
  const current = tabs.some((t) => t.id === tab) ? tab : "build";
  const content = {
    build: <BuildTab caravan={caravan} onCaravanChange={onCaravanChange} onReport={() => setReportOpen(true)} />,
    specs: caravan.model_specs && <MeasurementsPanel specs={caravan.model_specs} />,
    features: caravan.model_specs && <StandardFeatures specs={caravan.model_specs} />,
    warranty: <WarrantyHistory state={warranty} />,
    support: <SupportTickets state={support} />,
  }[current];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <CaravanHero caravan={caravan} onAskNick={() => navigate("/chat")} onClear={onClear} onJump={setTab} />
      </motion.div>
      <CaravanTabs tabs={tabs} active={current} onChange={setTab} />
      <AnimatePresence mode="wait">
        <motion.div key={current} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.22, ease: "easeOut" }} data-testid={`caravan-tab-panel-${current}`}>
          {content}
        </motion.div>
      </AnimatePresence>
      <ReportDiscrepancyModal open={reportOpen} onClose={() => setReportOpen(false)} caravan={caravan} />
    </div>
  );
}

export default function MyCaravan() {
  const { caravan, setCaravan, lookupCaravan, clearCaravan } = useCustomer();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (caravan?.chassis_number) lookupCaravan(caravan.chassis_number).catch(() => {});
    // refresh saved caravan once so new fields (specs, hubs) appear
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLookup = async (value) => {
    const code = (value || "").trim();
    if (!code) return;
    setLoading(true);
    try {
      await lookupCaravan(code);
      toast.success("Caravan found");
    } catch (err) {
      toast.error(getErrorMessage(err, "No caravan found for that chassis number"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer
      wide={!!caravan}
      overline="My Caravan"
      title="Your Caravan Details"
      subtitle={caravan ? null : "Enter your chassis number to load the exact equipment and configuration fitted to your On The Move caravan."}
    >
      {caravan ? (
        <CaravanDetails key={caravan.chassis_number} caravan={caravan} onCaravanChange={setCaravan} onClear={clearCaravan} />
      ) : (
        <LookupForm onLookup={runLookup} loading={loading} />
      )}
    </PageContainer>
  );
}
